import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Alert,
  Modal,
  TextInput,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { Button, Card, Chip, ActivityIndicator } from 'react-native-paper';
import { User, InvitationToken } from '../../types';
import { userService } from '../../services/userService';
import { theme } from '../../constants/theme';

interface UserManagementScreenProps {
  navigation?: any;
  onBack?: () => void;
}

const UserManagementScreen: React.FC<UserManagementScreenProps> = ({ navigation, onBack }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<InvitationToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'reviewer'>('reviewer');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');
  const [generatedInvitation, setGeneratedInvitation] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, invitationsData] = await Promise.all([
        userService.getAllUsers(),
        userService.getInvitations(),
      ]);
      
      // Ensure all invitations have proper links
      const invitationsWithLinks = invitationsData.map(inv => ({
        ...inv,
        invitationLink: inv.invitationLink || 
          `https://exhalehomereview-testing.alexmazure.com/invite/${inv.token}`
      }));
      
      setUsers(usersData);
      setInvitations(invitationsWithLinks);
    } catch (error) {
      console.error('Error loading user management data:', error);
      alert('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'reviewer' : 'admin';
    const action = newRole === 'admin' ? 'promote to admin' : 'demote to reviewer';
    
    if (window.confirm(`Are you sure you want to ${action}?`)) {
      try {
        const updatedUser = await userService.updateUserRole(userId, newRole);
        setUsers(users.map(user => 
          user.id === userId ? { ...user, role: updatedUser.role } : user
        ));
        alert(`User role updated to ${newRole}`);
      } catch (error) {
        console.error('Error updating user role:', error);
        alert('Failed to update user role');
      }
    }
  };

  const handleCreateInvitation = async () => {
    if (!inviteEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    try {
      setInviteLoading(true);
      const invitation = await userService.createInvitation(inviteEmail.trim(), inviteRole);

      // Ensure invitation has the proper link
      const invitationWithLink = {
        ...invitation,
        invitationLink: invitation.invitationLink || 
          `https://exhalehomereview-testing.alexmazure.com/invite/${invitation.token}`
      };
      
      // Check if email was sent successfully
      if (invitation.message && invitation.message.includes('sent')) {
        // Email was sent successfully
        alert(`✅ Invitation sent successfully to ${inviteEmail}`);
      } else if (invitation.warning) {
        // Email failed but invitation was created
        alert(`⚠️ ${invitation.warning}`);
        // Show the generated invitation in a popup as fallback
        const invitationWithLink = {
          ...invitation,
          invitationLink: invitation.invitationLink || 
            `https://exhalehomereview-testing.alexmazure.com/invite/${invitation.token}`
        };
        setGeneratedInvitation(invitationWithLink);
      } else {
        // Fallback for older API responses
        const invitationWithLink = {
          ...invitation,
          invitationLink: invitation.invitationLink || 
            `https://exhalehomereview-testing.alexmazure.com/invite/${invitation.token}`
        };
        setGeneratedInvitation(invitationWithLink);
      }
      
      setInviteEmail('');
      setInviteRole('reviewer');
      setShowInviteModal(false);
      loadData(); // Refresh invitations
    } catch (error) {
      console.error('Error creating invitation:', error);
      
      // Show specific error messages based on the error
      if (error.message.includes('409')) {
        alert('An active invitation already exists for this email address. Please check the Invitations tab or revoke the existing invitation first.');
      } else if (error.message.includes('User with this email already exists')) {
        alert('A user with this email address already has an account.');
      } else {
        alert('Failed to create invitation: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvitation = async (token: string, email: string) => {
    if (window.confirm(`Are you sure you want to resend the invitation to ${email}?`)) {
      try {
        const result = await userService.resendInvitation(token);
        alert('✅ ' + result.message);
      } catch (error) {
        console.error('Error resending invitation:', error);
        alert('Failed to resend invitation: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const handleRevokeInvitation = (token: string, email: string) => {
    if (window.confirm(`Are you sure you want to revoke the invitation for ${email}?`)) {
      userService.revokeInvitation(token)
        .then(() => {
          alert('Invitation revoked');
          loadData(); // Refresh invitations
        })
        .catch((error) => {
          console.error('Error revoking invitation:', error);
          alert('Failed to revoke invitation');
        });
    }
  };

  const handleDeleteUser = (userId: number, userName: string, userEmail: string) => {
    if (window.confirm(`Are you sure you want to permanently delete user "${userName}" (${userEmail})? This action cannot be undone.`)) {
      userService.deleteUser(userId)
        .then(() => {
          alert('User deleted successfully');
          loadData(); // Refresh users list
        })
        .catch((error) => {
          console.error('Error deleting user:', error);
          if (error.message.includes('Cannot delete your own account')) {
            alert('You cannot delete your own account.');
          } else {
            alert('Failed to delete user: ' + (error.message || 'Unknown error'));
          }
        });
    }
  };

  const renderUser = ({ item }: { item: User }) => (
    <Card style={styles.userCard}>
      <Card.Content>
        <View style={styles.userInfo}>
          <View style={styles.userDetails}>
            <Text style={styles.userName}>{item.firstName} {item.lastName}</Text>
            <Text style={styles.userEmail}>{item.email}</Text>
            <Chip 
              mode="outlined" 
              style={[styles.roleChip, item.role === 'admin' ? styles.adminChip : styles.reviewerChip]}
            >
              {item.role.toUpperCase()}
            </Chip>
          </View>
          <View style={styles.userActions}>
            <Button
              mode="outlined"
              onPress={() => handleRoleChange(item.id, item.role)}
              style={styles.roleButton}
            >
              {item.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
            </Button>
            <Button
              mode="outlined"
              onPress={() => handleDeleteUser(item.id, `${item.firstName} ${item.lastName}`, item.email)}
              style={[styles.roleButton, styles.deleteButton]}
            >
              Delete
            </Button>
          </View>
        </View>
      </Card.Content>
    </Card>
  );

  const renderInvitation = ({ item }: { item: InvitationToken }) => {
    const isExpired = new Date(item.expiresAt) < new Date();
    const isUsed = !!item.usedAt;
    
    return (
      <Card style={styles.invitationCard}>
        <Card.Content>
          <View style={styles.invitationInfo}>
            <View style={styles.invitationDetails}>
              <Text style={styles.invitationEmail}>{item.email}</Text>
              <Text style={styles.invitationInviter}>
                Invited by: {item.inviter?.firstName} {item.inviter?.lastName}
              </Text>
              <Text style={styles.invitationRole}>
                Role: {item.role?.toUpperCase() || 'REVIEWER'}
              </Text>
              <Text style={styles.invitationDate}>
                Expires: {new Date(item.expiresAt).toLocaleDateString()}
              </Text>
              <Chip 
                mode="outlined" 
                style={[
                  styles.statusChip, 
                  isUsed ? styles.usedChip : isExpired ? styles.expiredChip : styles.activeChip
                ]}
              >
                {isUsed ? 'USED' : isExpired ? 'EXPIRED' : 'ACTIVE'}
              </Chip>
              {!isUsed && !isExpired && (
                <TouchableOpacity 
                  style={styles.linkButton}
                  onPress={() => handleResendInvitation(item.token, item.email)}
                >
                  <Text style={styles.linkButtonText}>📧 Resend</Text>
                </TouchableOpacity>
              )}
            </View>
            {!isUsed && !isExpired && (
              <Button
                mode="outlined"
                onPress={() => handleRevokeInvitation(item.token, item.email)}
                style={styles.revokeButton}
              >
                Revoke
              </Button>
            )}
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading user management...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        {onBack && (
          <TouchableOpacity style={styles.backButton} onPress={onBack}>
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
        )}
        <Text style={styles.title}>User Management</Text>
        <Button
          mode="contained"
          onPress={() => setShowInviteModal(true)}
          style={styles.inviteButton}
        >
          Send Invite
        </Button>
      </View>

      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'users' && styles.activeTab]}
          onPress={() => setActiveTab('users')}
        >
          <Text style={[styles.tabText, activeTab === 'users' && styles.activeTabText]}>
            Users ({users.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'invitations' && styles.activeTab]}
          onPress={() => setActiveTab('invitations')}
        >
          <Text style={[styles.tabText, activeTab === 'invitations' && styles.activeTabText]}>
            Invitations ({invitations.length})
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.scrollContainer}>
        {activeTab === 'users' ? (
          <View style={styles.listContent}>
            {users.map((user) => (
              <View key={user.id.toString()}>
                {renderUser({ item: user })}
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.listContent}>
            {invitations.map((invitation) => (
              <View key={invitation.id.toString()}>
                {renderInvitation({ item: invitation })}
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <Modal
        visible={showInviteModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowInviteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Send Invitation</Text>
            <TextInput
              style={styles.emailInput}
              placeholder="Enter email address"
              value={inviteEmail}
              onChangeText={setInviteEmail}
              // keyboardType="email-address"
              autoCapitalize="none"
            />
            
            <Text style={styles.modalLabel}>Role:</Text>
            <View style={styles.roleSelector}>
              <TouchableOpacity
                style={[styles.roleOption, inviteRole === 'reviewer' && styles.roleOptionSelected]}
                onPress={() => setInviteRole('reviewer')}
              >
                <Text style={[styles.roleOptionText, inviteRole === 'reviewer' && styles.roleOptionTextSelected]}>
                  Home Reviewer
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.roleOption, inviteRole === 'admin' && styles.roleOptionSelected]}
                onPress={() => setInviteRole('admin')}
              >
                <Text style={[styles.roleOptionText, inviteRole === 'admin' && styles.roleOptionTextSelected]}>
                  Administrator
                </Text>
              </TouchableOpacity>
            </View>
            <View style={styles.modalButtons}>
              <Button
                mode="outlined"
                onPress={() => setShowInviteModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button
                mode="contained"
                onPress={handleCreateInvitation}
                loading={inviteLoading}
                disabled={inviteLoading}
                style={styles.sendButton}
              >
                Send Invite
              </Button>
            </View>
          </View>
        </View>
      </Modal>

      {/* Invitation Link Modal */}
      <Modal
        visible={!!generatedInvitation}
        transparent
        animationType="slide"
        onRequestClose={() => setGeneratedInvitation(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Invitation Generated!</Text>
            {generatedInvitation && (
              <>
                <Text style={styles.invitationDetails}>
                  Email: {generatedInvitation.email}
                </Text>
                <Text style={styles.invitationDetails}>
                  Role: {generatedInvitation.role?.toUpperCase() || 'REVIEWER'}
                </Text>
                <Text style={styles.modalLabel}>Invitation Link:</Text>
                <View style={styles.linkContainer}>
                  <Text style={styles.linkText} selectable>
                    {generatedInvitation.invitationLink}
                  </Text>
                </View>
                <View style={styles.modalButtons}>
                  <Button
                    mode="outlined"
                    onPress={() => {
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(generatedInvitation.invitationLink);
                        alert('Link copied to clipboard!');
                      } else {
                        alert('Please copy the link manually from above');
                      }
                    }}
                    style={styles.copyButton}
                  >
                    📋 Copy Link
                  </Button>
                  <Button
                    mode="contained"
                    onPress={() => setGeneratedInvitation(null)}
                    style={styles.closeButton}
                  >
                    Close
                  </Button>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.onSurface,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.outline,
    minHeight: 64,
  },
  backButton: {
    padding: 4,
    marginRight: 8,
    cursor: 'pointer',
    minWidth: 50,
  },
  backButtonText: {
    fontSize: 14,
    color: theme.colors.primary,
    fontWeight: 'bold',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.onSurface,
  },
  inviteButton: {
    marginLeft: 16,
  },
  tabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.outline,
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    cursor: 'pointer',
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.primary,
  },
  tabText: {
    fontSize: 16,
    color: theme.colors.onSurfaceVariant,
  },
  activeTabText: {
    color: theme.colors.primary,
    fontWeight: 'bold',
  },
  scrollContainer: {
    flex: 1,
  },
  listContent: {
    padding: 24,
  },
  userCard: {
    marginBottom: 16,
  },
  userInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.onSurface,
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: theme.colors.onSurfaceVariant,
    marginBottom: 8,
  },
  roleChip: {
    alignSelf: 'flex-start',
  },
  adminChip: {
    backgroundColor: theme.colors.errorContainer,
  },
  reviewerChip: {
    backgroundColor: theme.colors.primaryContainer,
  },
  roleButton: {
    marginLeft: 16,
  },
  userActions: {
    flexDirection: 'column',
    gap: 8,
  },
  deleteButton: {
    borderColor: theme.colors.error,
  },
  invitationCard: {
    marginBottom: 16,
  },
  invitationInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  invitationDetails: {
    flex: 1,
  },
  invitationEmail: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.onSurface,
    marginBottom: 4,
  },
  invitationInviter: {
    fontSize: 14,
    color: theme.colors.onSurfaceVariant,
    marginBottom: 2,
  },
  invitationDate: {
    fontSize: 14,
    color: theme.colors.onSurfaceVariant,
    marginBottom: 8,
  },
  invitationRole: {
    fontSize: 14,
    color: theme.colors.onSurfaceVariant,
    marginBottom: 2,
    fontWeight: 'bold',
  },
  statusChip: {
    alignSelf: 'flex-start',
  },
  activeChip: {
    backgroundColor: theme.colors.primaryContainer,
  },
  usedChip: {
    backgroundColor: theme.colors.surfaceVariant,
  },
  expiredChip: {
    backgroundColor: theme.colors.errorContainer,
  },
  revokeButton: {
    marginLeft: 16,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.colors.surface,
    padding: 32,
    borderRadius: 12,
    width: '90%',
    maxWidth: 500,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.onSurface,
    marginBottom: 24,
    textAlign: 'center',
  },
  emailInput: {
    borderWidth: 1,
    borderColor: theme.colors.outline,
    borderRadius: 8,
    padding: 16,
    fontSize: 16,
    marginBottom: 24,
    backgroundColor: theme.colors.background,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
  },
  cancelButton: {
    flex: 1,
  },
  sendButton: {
    flex: 1,
  },
  modalLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.onSurface,
    marginBottom: 12,
    marginTop: 16,
  },
  roleSelector: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  roleOption: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.colors.outline,
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
  },
  roleOptionSelected: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primaryContainer,
  },
  roleOptionText: {
    fontSize: 14,
    color: theme.colors.onSurface,
  },
  roleOptionTextSelected: {
    color: theme.colors.onPrimaryContainer,
    fontWeight: 'bold',
  },
  linkButton: {
    backgroundColor: theme.colors.primaryContainer,
    padding: 8,
    borderRadius: 6,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  linkButtonText: {
    fontSize: 12,
    color: theme.colors.onPrimaryContainer,
    fontWeight: 'bold',
  },
  linkContainer: {
    backgroundColor: theme.colors.surfaceVariant,
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: theme.colors.outline,
  },
  linkText: {
    fontSize: 14,
    color: theme.colors.onSurfaceVariant,
    fontFamily: 'monospace',
    textAlign: 'center',
  },
  copyButton: {
    flex: 1,
    marginRight: 8,
  },
  closeButton: {
    flex: 1,
    marginLeft: 8,
  },
});

export default UserManagementScreen;
